import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "consumer-theory-indifference-curves-utility-maximization",
  title: "Consumer Theory & Indifference Curves: Utility Maximization, Budget Constraints & Exam Numericals",
  description: "Comprehensive 4,000-word masterclass on Consumer Theory for DU Economics students. Covers utility functions, indifference curve properties, MRS derivation, budget constraint geometry, Lagrangian optimization, and 3 fully worked numerical problems.",
  pubDate: "2026-08-05",
  updatedDate: "2026-08-05",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["economics", "consumer-theory", "indifference-curves", "utility-maximization", "microeconomics"],
  readMinutes: 24,
  heroEyebrow: "Subject Explainers · Microeconomics",
};

export function Body() {
  return (
    <>
      <p>
        Consumer Theory is the foundational pillar of microeconomics. It explains how rational consumers allocate their limited income across goods and services to achieve maximum satisfaction (utility). For Delhi University B.A. (Hons) Economics students, Consumer Theory dominates Semester 1 Introductory Microeconomics and Semester 3 Intermediate Microeconomics I, appearing consistently as 15-mark and 20-mark exam questions.
      </p>

      <p>
        In this 4,000-word numerical masterclass, we derive the mathematical properties of utility functions, prove indifference curve convexity, derive the Marginal Rate of Substitution (MRS), set up the Lagrangian constrained optimization framework, solve for Marshallian and Hicksian demand functions, and work through 3 complete step-by-step examination numerical problems.
      </p>

      <h2>1. The Utility Function</h2>
      <p>
        A <strong>utility function</strong> U(x1, x2) assigns a numerical value to each consumption bundle (x1, x2) representing the consumer's preference ordering. If Bundle A is preferred to Bundle B, then U(A) &gt; U(B).
      </p>

      <h3>1.1 Properties of Well-Behaved Preferences</h3>
      <ul>
        <li><strong>Completeness:</strong> For any two bundles A and B, either A is preferred to B, B is preferred to A, or the consumer is indifferent.</li>
        <li><strong>Transitivity:</strong> If A is preferred to B, and B is preferred to C, then A is preferred to C.</li>
        <li><strong>Non-Satiation (Monotonicity):</strong> More of any good is always (weakly) preferred. If bundle A has more of at least one good and no less of any other, A is preferred to B.</li>
        <li><strong>Convexity:</strong> Averages are preferred to extremes. If the consumer is indifferent between A and B, then any weighted average t*A + (1-t)*B (where 0 &lt; t &lt; 1) is at least as good as A or B.</li>
      </ul>

      <h3>1.2 Common Utility Function Forms</h3>
      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Utility Type</th>
              <th className="p-3 border-r border-rule">Functional Form</th>
              <th className="p-3 border-r border-rule">IC Shape</th>
              <th className="p-3">MRS Property</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Cobb-Douglas</td>
              <td className="p-3 border-r border-rule font-mono text-xs">U = x1^a * x2^b</td>
              <td className="p-3 border-r border-rule">Smooth, strictly convex hyperbolas</td>
              <td className="p-3">Diminishing MRS</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Perfect Substitutes</td>
              <td className="p-3 border-r border-rule font-mono text-xs">U = a*x1 + b*x2</td>
              <td className="p-3 border-r border-rule">Straight lines with slope -a/b</td>
              <td className="p-3">Constant MRS = a/b</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Perfect Complements (Leontief)</td>
              <td className="p-3 border-r border-rule font-mono text-xs">U = min(a*x1, b*x2)</td>
              <td className="p-3 border-r border-rule">L-shaped with kink at a*x1 = b*x2</td>
              <td className="p-3">MRS undefined at kink; 0 or infinity elsewhere</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Quasi-Linear</td>
              <td className="p-3 border-r border-rule font-mono text-xs">U = v(x1) + x2</td>
              <td className="p-3 border-r border-rule">Vertically parallel curves</td>
              <td className="p-3">MRS depends only on x1</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>2. Indifference Curves: Definition &amp; Properties</h2>
      <p>
        An <strong>indifference curve</strong> is the locus of all consumption bundles that yield the same level of utility. Formally, for a given utility level u_bar, the indifference curve is:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        IC(u_bar) = {"{"} (x1, x2) : U(x1, x2) = u_bar {"}"}
      </div>

      <h3>2.1 Five Key Properties of Indifference Curves</h3>
      <ol className="list-decimal list-inside space-y-2 text-sm my-4">
        <li><strong>Downward Sloping:</strong> By non-satiation, if x1 increases, x2 must decrease to maintain the same utility. Hence ICs slope downward.</li>
        <li><strong>Higher ICs Represent Greater Utility:</strong> By monotonicity, a bundle on a higher IC (farther from origin) is strictly preferred.</li>
        <li><strong>ICs Cannot Intersect:</strong> If two ICs intersected at a point, transitivity would be violated. Proof: Suppose IC1 and IC2 cross at point P. Take point A on IC1 and point B on IC2 (different from P). Then A ~ P (same IC1), and B ~ P (same IC2), so by transitivity A ~ B. But A is on a different IC than B, implying different utility — contradiction.</li>
        <li><strong>ICs Are Thin:</strong> Each IC has zero thickness (it is a curve, not a band).</li>
        <li><strong>Convex to the Origin:</strong> By the convexity axiom, ICs bow inward toward the origin, reflecting diminishing MRS.</li>
      </ol>

      <h2>3. Marginal Rate of Substitution (MRS)</h2>
      <p>
        The <strong>MRS</strong> measures the rate at which a consumer is willing to trade Good 2 for Good 1 while maintaining the same utility level. It equals the (negative of the) slope of the indifference curve.
      </p>

      <h3>3.1 Derivation via Total Differential</h3>
      <p>
        Along an indifference curve, utility is constant: dU = 0. Taking the total differential of U(x1, x2):
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        dU = (dU/dx1)*dx1 + (dU/dx2)*dx2 = 0
      </div>
      <p>Rearranging:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        MRS = -dx2/dx1 = (dU/dx1) / (dU/dx2) = MU1 / MU2
      </div>
      <p>
        where MU1 = dU/dx1 is the marginal utility of Good 1, and MU2 = dU/dx2 is the marginal utility of Good 2.
      </p>

      <h3>3.2 Diminishing MRS</h3>
      <p>
        For well-behaved (strictly convex) preferences, the MRS diminishes as x1 increases along an IC. Intuitively, as you accumulate more of Good 1, each additional unit becomes less valuable relative to Good 2, so you are willing to give up less of Good 2 per unit of Good 1.
      </p>

      <h2>4. The Budget Constraint</h2>
      <p>
        A consumer with income M faces prices p1 and p2 for goods 1 and 2. The budget constraint defines the affordable set:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        p1*x1 + p2*x2 &lt;= M
      </div>
      <p>
        The budget line (where the constraint binds with equality) has slope <strong>-p1/p2</strong>, x1-intercept M/p1, and x2-intercept M/p2.
      </p>

      <h2>5. Utility Maximization: The Tangency Condition</h2>
      <p>
        The consumer chooses the bundle on the highest achievable IC that still satisfies the budget constraint. At the optimum (interior solution), the IC is tangent to the budget line:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        MRS = MU1/MU2 = p1/p2
      </div>
      <p>
        This is the <strong>equi-marginal principle</strong>: at the optimum, the marginal utility per rupee spent is equalized across all goods.
      </p>

      <h3>5.1 Lagrangian Method</h3>
      <p>
        Set up the constrained optimization problem:
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        Maximize U(x1, x2) subject to p1*x1 + p2*x2 = M
      </div>
      <p>Lagrangian:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        L = U(x1, x2) + lambda * (M - p1*x1 - p2*x2)
      </div>
      <p>First-Order Conditions (FOCs):</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        dL/dx1 = MU1 - lambda*p1 = 0  ==&gt;  MU1 = lambda*p1<br />
        dL/dx2 = MU2 - lambda*p2 = 0  ==&gt;  MU2 = lambda*p2<br />
        dL/dlambda = M - p1*x1 - p2*x2 = 0
      </div>
      <p>
        Dividing the first FOC by the second: MU1/MU2 = p1/p2, which is the tangency condition. The Lagrange multiplier lambda represents the <strong>marginal utility of income</strong>.
      </p>

      <h2>6. Worked Examination Numerical Problems</h2>

      <h3>Numerical Problem 1: Cobb-Douglas Utility Maximization (15 Marks)</h3>
      <p>
        <strong>Question:</strong> A consumer has utility function U(x1, x2) = x1^(1/3) * x2^(2/3). Prices are p1 = 4, p2 = 8, and income M = 240. Find the optimal consumption bundle and the maximum utility.
      </p>

      <h4>Solution:</h4>
      <p><strong>Step 1: Find Marginal Utilities</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        MU1 = dU/dx1 = (1/3) * x1^(-2/3) * x2^(2/3)<br />
        MU2 = dU/dx2 = (2/3) * x1^(1/3) * x2^(-1/3)
      </div>

      <p><strong>Step 2: Apply Tangency Condition MU1/MU2 = p1/p2</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        MRS = MU1/MU2 = [(1/3) * x2^(2/3) * x2^(1/3)] / [(2/3) * x1^(2/3) * x1^(1/3)]<br />
        = (1/3 * x2) / (2/3 * x1) = x2 / (2*x1)
      </div>
      <p>Setting MRS = p1/p2 = 4/8 = 1/2:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        x2 / (2*x1) = 1/2  ==&gt;  x2 = x1
      </div>

      <p><strong>Step 3: Substitute into Budget Constraint</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        4*x1 + 8*x2 = 240<br />
        4*x1 + 8*x1 = 240  (since x2 = x1)<br />
        12*x1 = 240
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        x1* = 20,  x2* = 20<br />
        U* = 20^(1/3) * 20^(2/3) = 20
      </div>

      <p><strong>Verification (Expenditure Share Property):</strong> For Cobb-Douglas U = x1^a * x2^b, the income share spent on Good 1 is a/(a+b). Here a = 1/3, b = 2/3, so share on Good 1 = (1/3) / (1) = 1/3. Expenditure on x1 = 4 * 20 = 80 = 240/3. Correct!</p>

      <h3>Numerical Problem 2: Perfect Substitutes (10 Marks)</h3>
      <p>
        <strong>Question:</strong> A consumer has utility U = 3*x1 + 5*x2. Prices are p1 = 6, p2 = 10, and income M = 120. Find the optimal bundle.
      </p>

      <h4>Solution:</h4>
      <p>For perfect substitutes, the consumer compares MU1/p1 vs MU2/p2:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        MU1/p1 = 3/6 = 0.5<br />
        MU2/p2 = 5/10 = 0.5
      </div>
      <p>
        Since MU1/p1 = MU2/p2, the consumer is indifferent between all bundles on the budget line. <strong>Any combination of x1 and x2 satisfying 6*x1 + 10*x2 = 120 is optimal.</strong>
      </p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Optimal Set = {"{"} (x1, x2) : 6*x1 + 10*x2 = 120, x1 &gt;= 0, x2 &gt;= 0 {"}"}<br />
        Maximum Utility = 3*(120/6) = 60 (or 5*(120/10) = 60 — same!)
      </div>

      <h3>Numerical Problem 3: Income and Substitution Effects (20 Marks)</h3>
      <p>
        <strong>Question:</strong> A consumer has utility U = x1 * x2. Initially p1 = 2, p2 = 4, M = 80. Price of Good 1 falls to p1' = 1. Decompose the change in demand for x1 into Substitution Effect and Income Effect using the Slutsky method.
      </p>

      <h4>Solution:</h4>
      <p><strong>Step 1: Find Initial Optimal Bundle (A)</strong></p>
      <p>For U = x1 * x2 (Cobb-Douglas with a = b = 1), optimal demands are:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        x1* = M/(2*p1) = 80/(2*2) = 20<br />
        x2* = M/(2*p2) = 80/(2*4) = 10<br />
        Bundle A = (20, 10)
      </div>

      <p><strong>Step 2: Find Final Optimal Bundle (C) after price change</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        x1** = M/(2*p1') = 80/(2*1) = 40<br />
        x2** = M/(2*p2) = 80/(2*4) = 10<br />
        Bundle C = (40, 10)
      </div>

      <p><strong>Step 3: Slutsky Decomposition — Find Compensated Income (M')</strong></p>
      <p>Slutsky compensation: M' = new prices * original bundle:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        M' = p1'*x1_A + p2*x2_A = 1*20 + 4*10 = 60
      </div>

      <p><strong>Step 4: Find Intermediate Bundle (B) at new prices with compensated income</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        x1_B = M'/(2*p1') = 60/(2*1) = 30<br />
        Bundle B = (30, 7.5)
      </div>

      <p><strong>Step 5: Decompose Total Effect</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Total Effect = x1** - x1* = 40 - 20 = +20 units<br />
        Substitution Effect (A to B) = x1_B - x1* = 30 - 20 = +10 units<br />
        Income Effect (B to C) = x1** - x1_B = 40 - 30 = +10 units
      </div>
      <p>
        Both effects are positive (Good 1 is a <strong>normal good</strong>): the price decrease makes it relatively cheaper (substitution effect) and increases real purchasing power (income effect).
      </p>

      <h2>7. Summary Checklist for DU Exams</h2>
      <ul>
        <li>Always state the tangency condition MRS = p1/p2 before solving.</li>
        <li>For Cobb-Douglas, use the expenditure share shortcut to verify answers.</li>
        <li>For perfect substitutes, compare MU/p ratios — corner solutions are typical.</li>
        <li>For perfect complements, set a*x1 = b*x2 and substitute into the budget constraint.</li>
        <li>In Slutsky decomposition, always clearly label Bundles A, B, and C.</li>
      </ul>

      <p>
        For related microeconomics guides, see our <a href="/blog/elasticity-of-demand-indian-examples" className="font-semibold underline text-grade">Elasticity of Demand Masterclass</a> and <a href="/blog/game-theory-nash-equilibrium-du-economics-guide" className="font-semibold underline text-grade">Game Theory &amp; Nash Equilibrium Guide</a>.
      </p>
    </>
  );
}


